import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Plus, Edit, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BatchManagement = ({ onUpdate }) => {
  const [batches, setBatches] = useState([]);
  const [courses, setCourses] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [formData, setFormData] = useState({
    course_id: '',
    batch_name: '',
    schedule: '',
    capacity: 15
  });

  useEffect(() => {
    fetchBatches();
    fetchCourses();
  }, []);

  const fetchBatches = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/batches`, { withCredentials: true });
      setBatches(response.data);
    } catch (error) {
      toast.error('Failed to fetch batches');
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/courses`, { withCredentials: true });
      setCourses(response.data);
    } catch (error) {
      toast.error('Failed to fetch courses');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBatch) {
        await axios.put(`${BACKEND_URL}/api/batches/${editingBatch.batch_id}`, formData, { withCredentials: true });
        toast.success('Batch updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/batches`, formData, { withCredentials: true });
        toast.success('Batch created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchBatches();
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const deleteBatch = async (batchId) => {
    if (!window.confirm('Delete this batch?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/batches/${batchId}`, { withCredentials: true });
      toast.success('Batch deleted');
      fetchBatches();
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const resetForm = () => {
    setFormData({ course_id: '', batch_name: '', schedule: '', capacity: 15 });
    setEditingBatch(null);
  };

  const openEditDialog = (batch) => {
    setEditingBatch(batch);
    setFormData({
      course_id: batch.course_id,
      batch_name: batch.batch_name,
      schedule: batch.schedule,
      capacity: batch.capacity
    });
    setIsDialogOpen(true);
  };

  const getCourseName = (courseId) => {
    const course = courses.find(c => c.course_id === courseId);
    return course ? course.name : 'Unknown Course';
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Manage Batches</h3>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-purple-700 hover:bg-purple-800">
              <Plus className="h-4 w-4 mr-2" />
              Add Batch
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingBatch ? 'Edit Batch' : 'Create New Batch'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Course</label>
                <select
                  value={formData.course_id}
                  onChange={(e) => setFormData({ ...formData, course_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                >
                  <option value="">Select Course</option>
                  {courses.filter(c => c.is_active).map(course => (
                    <option key={course.course_id} value={course.course_id}>{course.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Batch Name</label>
                <Input
                  value={formData.batch_name}
                  onChange={(e) => setFormData({ ...formData, batch_name: e.target.value })}
                  placeholder="e.g., Morning Batch, Weekend Batch"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Schedule</label>
                <Input
                  value={formData.schedule}
                  onChange={(e) => setFormData({ ...formData, schedule: e.target.value })}
                  placeholder="e.g., Mon, Wed, Fri - 9:00 AM to 11:00 AM"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Capacity</label>
                <Input
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) })}
                  min="1"
                  required
                />
              </div>
              <Button type="submit" className="w-full bg-purple-700 hover:bg-purple-800">
                {editingBatch ? 'Update Batch' : 'Create Batch'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {batches.map((batch) => (
          <div key={batch.batch_id} className="border border-purple-200 rounded-lg p-4 bg-white hover:shadow-md">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900">{batch.batch_name}</h4>
                <p className="text-sm text-purple-600 mt-1">{getCourseName(batch.course_id)}</p>
                <p className="text-sm text-gray-600 mt-1">{batch.schedule}</p>
                <div className="flex gap-2 mt-2">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                    {batch.enrolled_count}/{batch.capacity} students
                  </span>
                  {batch.is_active && <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">Active</span>}
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => openEditDialog(batch)} variant="ghost" size="sm">
                  <Edit className="h-4 w-4 text-blue-600" />
                </Button>
                <Button onClick={() => deleteBatch(batch.batch_id)} variant="ghost" size="sm">
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {batches.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          No batches yet. Create your first batch!
        </div>
      )}
    </div>
  );
};

export default BatchManagement;
